import React from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Link,
  Theme,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { DownloadLink } from "components/shared/Download";
import { GTF_FILE, BED_FILE } from "helpers/apis";
import useAuth from "hooks/useAuth";
import useCatalogDetail from "hooks/useCatalogDetail";
import useDialog from "hooks/useDialog";
import { DesignType } from "types/design";
import { colors } from "theme";

const useStyles = makeStyles((theme: Theme) => ({
  card: { backgroundColor: colors.white },
  cardContent: { paddingTop: 0 },
  downloadList: {
    listStyleType: "none",
    "& li": {
      width: "100%",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      position: "relative",
      "& + li": { marginTop: theme.spacing(2) },
      [theme.breakpoints.down("lg")]: {
        "& button": { textAlign: "left" },
      },
    },
  },
  sectionTop: {
    [theme.breakpoints.up("sm")]: { marginBottom: theme.spacing(3) },
    [theme.breakpoints.down("sm")]: { marginBottom: theme.spacing(2) },
  },
  sectionTitle: {
    [theme.breakpoints.down("sm")]: {
      fontWeight: 600,
      fontSize: 21,
      lineHeight: "auto",
      "& a": { fontSize: 14 },
    },
  },
}));

const FilesCard: React.FC<{
  children: React.ReactNode;
  title: string;
}> = ({ children, title }) => {
  const classes = useStyles();

  return (
    <Card className={classes.card} square>
      <CardHeader
        title={title}
        titleTypographyProps={{
          component: "h3",
          variant: "h5",
          color: "inherit",
        }}
      />
      <CardContent>{children}</CardContent>
    </Card>
  );
};

const Files = () => {
  const { isLoggedIn } = useAuth();
  const { setOpenLoginDialog } = useDialog();
  const classes = useStyles();
  const { catalog, isCustomPanel } = useCatalogDetail();

  return (
    <section>
      <Box className={classes.sectionTop}>
        {isLoggedIn && (
          <Typography variant="h2" className={classes.sectionTitle}>
            Files
          </Typography>
        )}
        {!isLoggedIn && (
          <Typography variant="h2" className={classes.sectionTitle}>
            Files - To download files, please{" "}
            <Link color="secondary" onClick={() => setOpenLoginDialog(true)}>
              sign in
            </Link>
            .
          </Typography>
        )}
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FilesCard title="Available files for download">
            <Box
              component="ul"
              mt={-2}
              ml={-1}
              mb={0}
              className={classes.downloadList}
            >
              <li>
                <DownloadLink
                  requestConfig={{
                    url: BED_FILE,
                    params: {
                      [!isCustomPanel ? "designId" : "customPanelId"]:
                        catalog?.id,
                    },
                  }}
                  disabled={!isLoggedIn}
                >
                  Download Target Coverage BED File
                </DownloadLink>
              </li>
              <li>
                <DownloadLink
                  requestConfig={{
                    url: GTF_FILE,
                    params: {
                      [!isCustomPanel ? "designId" : "customPanelId"]:
                        catalog?.id,
                    },
                  }}
                  disabled={!isLoggedIn}
                >
                  Download GTF File
                </DownloadLink>
              </li>
            </Box>
          </FilesCard>
        </Grid>
        {catalog?.designType === DesignType.CATALOG_PANEL && (
          <Grid item xs={12} md={6}>
            <FilesCard title="Additional resources">
              <Box
                component="ul"
                mt={-2}
                ml={-1}
                mb={0}
                className={classes.downloadList}
              >
                <li>
                  <Link
                    component="button"
                    variant="body1"
                    color="secondary"
                    underline="always"
                    disabled={!isLoggedIn}
                    onClick={() => {
                      window.open(
                        "https://www.idtdna.com/pages/support/archer-support",
                        "_blank"
                      );
                    }}
                  >
                    View Protocol
                  </Link>
                </li>
                <li>
                  <Link
                    component="button"
                    variant="body1"
                    color="secondary"
                    underline="always"
                    disabled={!isLoggedIn}
                    type="button"
                    onClick={(evt: any) => {
                      evt.preventDefault();
                      window.open(
                        "https://www.idtdna.com/pages/support/archer-support",
                        "_blank"
                      );
                    }}
                  >
                    View Product Insert
                  </Link>
                </li>
              </Box>
            </FilesCard>
          </Grid>
        )}
      </Grid>
    </section>
  );
};

export default Files;
